import {
  acceptedInvitationSchema,
  API_ROUTES,
  createdInvitationSchema,
  invitationPreviewSchema,
  invitationSchema,
  membershipSchema,
  type InviteMemberRequest,
  type RegisterWithInvitationRequest,
  type UpdateMembershipRoleRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

export const peopleKeys = {
  all: (organizationId: string) => ['people', organizationId] as const,
  members: (organizationId: string) => ['people', organizationId, 'members'] as const,
  invitations: (organizationId: string) => ['people', organizationId, 'invitations'] as const,
};

export const membersQuery = (organizationId: string) =>
  queryOptions({
    queryKey: peopleKeys.members(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.memberships.list(organizationId),
        schema: z.array(membershipSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export const invitationsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: peopleKeys.invitations(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.invitations.list(organizationId),
        schema: z.array(invitationSchema),
        signal,
      }),
    staleTime: 30_000,
  });

export const changeMemberRole = (
  organizationId: string,
  membershipId: string,
  body: UpdateMembershipRoleRequest,
) =>
  apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.memberships.byId(organizationId, membershipId),
    schema: membershipSchema,
    body,
  });

export const revokeMember = (organizationId: string, membershipId: string) =>
  apiClient.request({
    method: 'DELETE',
    path: API_ROUTES.memberships.byId(organizationId, membershipId),
    schema: membershipSchema,
  });

/** Resolves to the invitation and its link, which is shown once and never stored. */
export const inviteMember = (organizationId: string, body: InviteMemberRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.invitations.list(organizationId),
    schema: createdInvitationSchema,
    body,
  });

export const withdrawInvitation = (organizationId: string, invitationId: string) =>
  apiClient.request({
    method: 'DELETE',
    path: API_ROUTES.invitations.byId(organizationId, invitationId),
    schema: invitationSchema,
  });

/* ---- the invite link, before and after sign-in ---------------------------- */

export const invitationPreviewQuery = (token: string) =>
  queryOptions({
    queryKey: ['invitation', token] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.invitations.preview(token),
        schema: invitationPreviewSchema,
        signal,
      }),
    retry: false,
    staleTime: 0,
  });

export const registerWithInvitation = (token: string, body: RegisterWithInvitationRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.invitations.register(token),
    schema: z.undefined(),
    body,
  });

export const acceptInvitation = (token: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.invitations.accept(token),
    schema: acceptedInvitationSchema,
  });
